import { zodResolver } from '@hookform/resolvers/zod';
import { isExternalRouter } from 'auth/cognito';
import { AxiosError } from 'axios';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import { FieldInputLength } from 'components/FieldInputLength';
import { ImagePicker } from 'components/misc/ImagePicker';
import { showErrorNotification, showNotification } from 'components/ShowNotification';
import { CustomerWithReference, ReferenceModel } from 'generated-types/skills/customer_references';
import { useLocalStorage } from 'hooks/misc/useLocalStorage';
import { useCompaniesHook } from 'hooks/skills/useCompanies';
import { useCustomerReferencesHook } from 'hooks/skills/useCustomerReferences';
import { useReferenceNotesHook } from 'hooks/skills/useReferenceNotes';
import { useMyExternalProfile, useProfile } from 'hooks/useProfile';
import React, { useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Button, ButtonGroup, Checkbox, FlexboxGrid, Input, Panel, Tooltip, Whisper } from 'rsuite';
import type { FileType } from 'rsuite/Uploader';
import {
    EditCustomerReferenceForm,
    EditCustomerReferenceFormType,
    referenceNotesFieldMaxLength,
    referenceQuoteByFieldMaxLength,
    referenceQuoteFieldMaxLength,
    referenceTextFieldMaxLength,
} from 'schema/customer_references/reference';

import { ErrorParagraph } from '../log-hours/styles';

export const CustomerReferenceEditForm = ({
    reference,
    customer,
    setEditMode,
    refetchReferences,
    customersWithoutReferenceRefetch,
}: {
    reference: ReferenceModel;
    customer: CustomerWithReference;
    setEditMode: React.Dispatch<React.SetStateAction<boolean>>;
    refetchReferences: () => void;
    customersWithoutReferenceRefetch: () => void;
}) => {
    const { updateReference, deleteReference } = useCustomerReferencesHook();
    const { referenceNotesData, refetchReferenceNotes, isReferenceNotesLoading } =
        useReferenceNotesHook(customer.customer.circles_customer_id);
    const [selectedBackgroundImage, setSelectedBackgroundImage] = useState<FileType | undefined>(
        undefined,
    );
    const [selectedLogo, setSelectedLogo] = useState<FileType | undefined>(undefined);
    const { createReference } = useCustomerReferencesHook();

    const useCompanies = useCompaniesHook(isExternalRouter);

    const hookToUseForProfile = isExternalRouter ? useMyExternalProfile : useProfile;
    const { user } = hookToUseForProfile();

    const { companies, refetch: refetchCompanies } = useCompanies({
        enabled: true,
        member_id: user?.id,
    });

    const {
        formState: { errors },
        watch,
        control,
        handleSubmit,
        register,
        setValue,
    } = useForm<EditCustomerReferenceFormType>({
        resolver: zodResolver(EditCustomerReferenceForm),
    });

    const [theme] = useLocalStorage<'light' | 'dark'>('theme', 'light');

    const onClose = () => {
        setEditMode(false);
    };

    const onGenerateNotes = () => {
        refetchReferenceNotes();
        setValue('notes', referenceNotesData);
    };

    const handleDelete = (reference_uuid: string) => {
        deleteReference.mutate(reference_uuid, {
            onSuccess: () => {
                showNotification({ header: 'Reference deleted successfully.' });
                refetchReferences();
                setEditMode(false);
            },
            onError: (error: AxiosError) => {
                showErrorNotification(error);
            },
        });
    };

    const onSave: SubmitHandler<EditCustomerReferenceFormType> = async data => {
        // if new image has been selected we add that, else we keep the current image_url
        if (selectedBackgroundImage) {
            data.image_file = selectedBackgroundImage.blobFile;
        } else {
            data.image_url = reference.image_url;
        }
        if (selectedLogo) {
            data.logo_file = selectedLogo.blobFile;
        } else {
            data.logo_url = reference.logo_url;
        }

        const reference_uuid = data.reference_uuid;
        const customer_id = customer.customer.circles_customer_id;

        if (reference_uuid) {
            updateReference.mutate(data, {
                onSuccess: () => {
                    setEditMode(false);
                    showNotification({ header: 'Reference edited successfully.' });
                    refetchReferences();
                },
                onError: (error: AxiosError) => {
                    showErrorNotification(error);
                },
            });
        } else {
            createReference.mutate(data, customer_id, {
                onSuccess: async () => {
                    await Promise.all([refetchCompanies(), refetchReferences()]);
                    showNotification({ header: 'New reference created successfully.' });
                    customersWithoutReferenceRefetch();
                },
                onError: (error: AxiosError) => {
                    showErrorNotification(error);
                },
            });
        }
    };

    const watchFields = watch();

    return (
        <form onSubmit={handleSubmit(onSave)}>
            <input hidden {...register('reference_uuid')} defaultValue={reference.reference_uuid} />
            <Panel
                bordered
                shaded
                key={reference.reference_uuid}
                style={{
                    marginBottom: 20,
                    background: 'white',
                    color: theme === 'dark' ? '#0f131a' : 'inherit',
                }}
            >
                <h5>{customer.customer.name}</h5>
                <FlexboxGrid justify="space-between" style={{ gap: '1rem' }}>
                    <FlexboxGrid.Item
                        colspan={11}
                        style={{ display: 'flex', justifyContent: 'flex-start' }}
                    >
                        <Controller
                            name="permission_to_use"
                            defaultValue={reference.permission_to_use}
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    checked={field.value}
                                    onChange={(_value, checked) => field.onChange(checked)}
                                >
                                    Permission to use as a reference
                                </Checkbox>
                            )}
                        />
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item
                        colspan={11}
                        style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                        <ButtonGroup>
                            <Button
                                appearance="primary"
                                color="green"
                                type="submit"
                                loading={updateReference.isLoading}
                            >
                                Save
                            </Button>
                            <Button onClick={() => onClose()}>Close</Button>
                        </ButtonGroup>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item colspan={11}>
                        <p>
                            <strong>
                                {reference.presigned_url
                                    ? 'Current Background Image'
                                    : 'No Background Image currently saved.'}
                            </strong>
                            <span style={{ color: 'red' }}>*</span>
                        </p>
                        <img src={reference.presigned_url} style={{ maxHeight: 150 }} />
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item colspan={11}>
                        <p>
                            <strong>
                                {reference.logo_presigned_url
                                    ? 'Current Logo'
                                    : 'No Logo currently saved.'}
                            </strong>
                        </p>
                        <img src={reference.logo_presigned_url} style={{ maxHeight: 150 }} />
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item colspan={11}>
                        <span>
                            Reference background image will be streched by width to fit the slide.
                            Use dimension 2600 x 1000 (w x h) or similar if you want whole image to
                            fit to the reserved space on slide.
                        </span>
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item colspan={11}>
                        <span>
                            Logo (optional) will be positioned in top left corner of the slide
                            before customer name.
                        </span>
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item colspan={11}>
                        <ImagePicker
                            value={selectedBackgroundImage}
                            label="Select New Background Image"
                            onFileSelect={file => setSelectedBackgroundImage(file)}
                        />
                    </FlexboxGrid.Item>
                    <FlexboxGrid.Item colspan={11}>
                        <ImagePicker
                            value={selectedLogo}
                            label="Select New Logo"
                            onFileSelect={file => setSelectedLogo(file)}
                        />
                    </FlexboxGrid.Item>

                    <FlexboxGrid.Item colspan={24} style={{ marginTop: '1rem' }}>
                        <p>
                            <strong>Customer Reference Title</strong>
                            <span style={{ color: 'red' }}>*</span>
                        </p>
                        <Controller
                            name="reference_title"
                            control={control}
                            defaultValue={
                                reference.reference_title !== ''
                                    ? reference.reference_title
                                    : customer.customer.name
                            }
                            render={({ field }) => (
                                <>
                                    <Input size="lg" as="textarea" rows={1} {...field} />
                                    <FieldInputLength
                                        currentLength={watchFields.reference_title?.length}
                                        maxLength={referenceQuoteByFieldMaxLength}
                                    />
                                </>
                            )}
                        />
                        <ErrorParagraph>{errors?.reference_title?.message}</ErrorParagraph>
                        <p>
                            <strong>Challenge</strong>
                            <span style={{ color: 'red' }}>*</span>
                        </p>
                        <Controller
                            name="challenge"
                            control={control}
                            defaultValue={reference.challenge}
                            render={({ field }) => (
                                <>
                                    <Input size="lg" as="textarea" rows={4} {...field} />
                                    <FieldInputLength
                                        currentLength={watchFields.challenge?.length}
                                        maxLength={referenceTextFieldMaxLength}
                                    />
                                </>
                            )}
                        />
                        <ErrorParagraph>{errors?.challenge?.message}</ErrorParagraph>
                        <p>
                            <strong>Solution</strong>
                            <span style={{ color: 'red' }}>*</span>
                        </p>
                        <Controller
                            name="solution"
                            control={control}
                            defaultValue={reference.solution}
                            render={({ field }) => (
                                <>
                                    <Input size="lg" as="textarea" rows={4} {...field} />
                                    <FieldInputLength
                                        currentLength={watchFields.solution?.length}
                                        maxLength={referenceTextFieldMaxLength}
                                    />
                                </>
                            )}
                        />
                        <ErrorParagraph>{errors?.solution?.message}</ErrorParagraph>
                        <p>
                            <strong>Quote</strong>
                        </p>
                        <Controller
                            name="quote"
                            control={control}
                            defaultValue={reference.quote}
                            render={({ field }) => (
                                <>
                                    <Input size="lg" as="textarea" rows={4} {...field} />
                                    <FieldInputLength
                                        currentLength={watchFields.quote?.length}
                                        maxLength={referenceQuoteFieldMaxLength}
                                    />
                                </>
                            )}
                        />
                        <ErrorParagraph>{errors?.quote?.message}</ErrorParagraph>
                        <FlexboxGrid justify="end" style={{ marginBottom: '1.5rem', gap: '1rem' }}>
                            <FlexboxGrid.Item colspan={11}>
                                <Controller
                                    name="quote_by"
                                    defaultValue={reference.quote_by}
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Whisper
                                                trigger="hover"
                                                placement="top"
                                                speaker={
                                                    <Tooltip>Name of the quoted person</Tooltip>
                                                }
                                            >
                                                <Input
                                                    size="md"
                                                    as="textarea"
                                                    maxLength={referenceQuoteByFieldMaxLength}
                                                    rows={1}
                                                    {...field}
                                                    placeholder="Quoted by (Name)"
                                                />
                                            </Whisper>
                                            <FieldInputLength
                                                currentLength={watchFields.quote_by?.length}
                                                maxLength={referenceQuoteByFieldMaxLength}
                                            />
                                        </>
                                    )}
                                />
                                <ErrorParagraph>{errors?.quote_by?.message}</ErrorParagraph>
                            </FlexboxGrid.Item>
                            <FlexboxGrid.Item colspan={11}>
                                <Controller
                                    name="quote_by_title"
                                    defaultValue={reference.quote_by_title}
                                    control={control}
                                    render={({ field }) => (
                                        <>
                                            <Whisper
                                                trigger="hover"
                                                placement="top"
                                                speaker={
                                                    <Tooltip>
                                                        Title/job descriptions of the quoted person
                                                    </Tooltip>
                                                }
                                            >
                                                <Input
                                                    size="md"
                                                    as="textarea"
                                                    maxLength={referenceQuoteByFieldMaxLength}
                                                    rows={1}
                                                    {...field}
                                                    placeholder="Quoted by (Title)"
                                                />
                                            </Whisper>
                                            <FieldInputLength
                                                currentLength={watchFields.quote_by_title?.length}
                                                maxLength={referenceQuoteByFieldMaxLength}
                                            />
                                        </>
                                    )}
                                />
                                <ErrorParagraph>{errors?.quote_by_title?.message}</ErrorParagraph>
                            </FlexboxGrid.Item>
                        </FlexboxGrid>
                        <p>
                            <strong>
                                <i>Speaker Notes</i>
                            </strong>
                            <span style={{ color: 'red' }}>*</span>
                            <span style={{ paddingLeft: 20 }}>
                                <Button
                                    appearance="primary"
                                    color="green"
                                    size="xs"
                                    style={{ width: 100 }}
                                    onClick={() => onGenerateNotes()}
                                    disabled={
                                        updateReference.isLoading || deleteReference.isLoading
                                    }
                                    loading={isReferenceNotesLoading}
                                >
                                    Regenerate
                                </Button>
                            </span>
                        </p>
                        <Controller
                            name="notes"
                            control={control}
                            defaultValue={reference.notes}
                            render={({ field }) => (
                                <>
                                    <Input
                                        size="lg"
                                        style={{ fontStyle: 'italic' }}
                                        as="textarea"
                                        rows={6}
                                        {...field}
                                    />
                                </>
                            )}
                        />
                        <ErrorParagraph>{errors?.notes?.message}</ErrorParagraph>

                        <FlexboxGrid justify="end">
                            <FlexboxGrid.Item>
                                <ConfirmationDialog
                                    handleOK={() => handleDelete(reference.reference_uuid)}
                                    header={'Delete Customer Reference'}
                                    message={
                                        'Are you sure you want to delete this customer reference?'
                                    }
                                >
                                    <Button
                                        appearance="primary"
                                        color="red"
                                        loading={deleteReference.isLoading}
                                    >
                                        Delete
                                    </Button>
                                </ConfirmationDialog>
                            </FlexboxGrid.Item>
                        </FlexboxGrid>
                    </FlexboxGrid.Item>
                </FlexboxGrid>
            </Panel>
        </form>
    );
};
